import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
	DataGridPremium,
	GridActionsCellItem,
	gridColumnDefinitionsSelector,
	gridColumnVisibilityModelSelector,
	gridFilterModelSelector,
	gridPageSelector,
	gridPageSizeSelector,
	gridSortModelSelector,
	gridVisibleColumnDefinitionsSelector,
	useGridApiContext,
	useGridSelector
} from '@mui/x-data-grid-premium';
import { useFormik } from 'formik';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import strings from '../../Globalization';
import consts from '../../consts';
import { useDidUpdate, useLiveRef, useSetState, useUniqueEffect } from '../../hooks';
import connectionService from '../../services/Connection';
import util from '../../util';
import ConfirmDialog from '../confirm';
import { useGridBase } from './GridBaseContext';

const sortRegex = /(\w+)(ASC|DESC)?/gi; //Example = descendingSortColumn DESC, ascendingSortColumn ASC
export const convertDefaultSort = (defaultSort) => {
	const orderBy = [];
	if (typeof defaultSort === 'string') {
		const sortFields = defaultSort.split(',');
		for (const sortField of sortFields) {
			const sortInfo = sortField.match(sortRegex);
			if (sortInfo?.length) {
				const [field, direction = 'ASC'] = sortInfo;
				orderBy.push({ field: field.trim(), sort: direction?.trim().toLowerCase() });
			}
		}
	}
	return orderBy;
};

const initialState = {
	preferences: [],
	selectedIds: [],
	confirmDeleteOpen: false,
	activeIndex: null, //Starts as null and changes to index or -1 onFetch
	formOpen: null,
	menuAnchorEl: null,
	dialogContent: 'list',
	dialogOpen: false,
	dialogWidth: 'md',
	fetching: true,
	saving: false,
	ready: false
};
const GridPreferencesControl = ({
	gridId,
	modelConfig,
	standaloneFilters,
	defaultSort,
	onChange,
	defaultColumnVisibilityModel,
	searchCapture = ['id'],
	defaultColumnWidths
}) => {
	const { gridColumns } = useGridBase();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const apiRef = useGridApiContext();
	const exportedState = apiRef?.current.exportState();
	const columnDefinations = useGridSelector(apiRef, gridColumnDefinitionsSelector);
	const page = useGridSelector(apiRef, gridPageSelector);
	const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
	const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
	const sortModel = useGridSelector(apiRef, gridSortModelSelector);
	const visibleColumns = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
	const visibleColumnDefinitions = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
	const columnVisibilityModel = useMemo(() => {
		const visibleColumnFields = {};
		Object.keys(visibleColumns).forEach((field) => {
			visibleColumnFields[field] = visibleColumns[field] && gridColumns.some((column) => column.field === field && !column.hide);
		});
		return visibleColumnFields;
	},[gridColumns, visibleColumns]);
	
	
	const pinnedColumns = { left: [], right: [], ...exportedState?.pinnedColumns };
	const versionedGridId = useMemo(() => {
		const contextSearch = new URLSearchParams();

		if (Array.isArray(searchCapture)) {
			searchCapture.forEach((param) => {
				if (searchParams.has(param)) {
					const contextParam = searchParams.get(param);
					contextSearch.append(param, contextParam);
				}
			});
		}

		const context = `${location.pathname}${contextSearch.size > 0 ? '?' : ''}${contextSearch.toString()}`;
		const encodedContext = btoa(context);
		return `${gridId}-${encodedContext}`;
	}, [location.pathname, gridId, searchParams]);

	const columnWidthsRef = useRef(Array.isArray(visibleColumnDefinitions)
		? visibleColumnDefinitions.map(({ width, field }) => ({ width, field }))
		: []);

	const defaultPreference = useMemo(() => {
		return {
			prefValue: {
				standaloneFilters: standaloneFilters,
				sorting: {
					sortModel:
						typeof defaultSort === 'string' || typeof modelConfig?.defaultSort === 'string'
							? convertDefaultSort(defaultSort || modelConfig?.defaultSort)
							: modelConfig?.defaultSort || []
				},
				filter: {
					filterModel: {
						items: []
					}
				},
				pagination: {
					pageSize: modelConfig.pageSize || consts.defaultPageSize,
					page: 0
				},
				columns: {
					columnVisibilityModel: defaultColumnVisibilityModel,
					columnOrder: Object.keys(defaultColumnVisibilityModel)
				},
				columnWidths:  defaultColumnWidths,
				pinnedColumns
			},
			prefId: 0,
			id: 0,
			prefName: strings.Default || 'Default',
			prefDesc: strings.Default || 'Default Preference',
			isSystemDefault: true
		};
	}, [versionedGridId, defaultColumnWidths]);
	const [state, setState] = useSetState(initialState);
	const theme = useTheme();
	const currentPreference = useMemo(
		() => {
			if(!state.ready || state.fetching) {
				return null;
			}
			const isDefaultPreference = state.activeIndex <= -1;
			return isDefaultPreference ? defaultPreference : state.preferences[state.activeIndex];
		},
		[state.activeIndex]
	);
	//
	const validationSchema = useMemo(() => {
		const existingNames = state.preferences.reduce((acc, curr) => {
			acc[curr.id] = curr.prefName;
			return acc;
		}, {});
		let schema = yup.object({
			prefName: yup
				.string()
				.required(
					strings.requiredField
						? strings.requiredField.replace('{0}', strings.Name || 'Name')
						: 'Preference Name is Reqiured'
				)
				.max(20, 'Maximum Length is 20')
				.test('unique-prefName', `${strings.Name || 'Name'} exists`, (val, { parent }) => {
					const id = parent?.id || 0;
					val = val?.trim?.();
					const indexOfIdName = id ? Object.keys(existingNames).indexOf(id) : -1;
					const names = Object.values(existingNames);
					const exists = names.includes(val) && (indexOfIdName === -1 || names[indexOfIdName] !== val);
					return !exists;
				}),
			prefDesc: yup.string().max(100, `${strings.Description} maximum length is 100`)
		});
		return schema;
	}, [state.preferences]);

	const defaultPreferenceIndex = useMemo(() => {
		const index = state.preferences.findIndex((pref) => pref.isDefault);
		return index;
	}, [state.preferences]);




	const handleOnChange = useCallback((pref) => {
		if (typeof onChange === 'function' && pref?.prefValue && state.ready) {
			/* if (pref?.prefValue?.columnWidths?.length > 0) {
				pref?.prefValue?.columnWidths?.forEach((columnWidth) => {
					if (typeof columnWidth?.field === 'string' && typeof columnWidth?.width === 'number') {
						apiRef.current.setColumnWidth(columnWidth.field, columnWidth.width);
					}
				});
			} else if (columnWidthsRef.current?.length > 0) {
				columnWidthsRef.current?.forEach((columnWidth) => {
					if (typeof columnWidth?.field === 'string' && typeof columnWidth?.width === 'number') {
						apiRef.current.setColumnWidth(columnWidth.field, columnWidth.width);
					}
				});
			}  */
			if (pref?.prefValue?.pinnedColumns) {
				apiRef.current.setPinnedColumns(pref?.prefValue?.pinnedColumns);
			} else {
				apiRef.current.setPinnedColumns(defaultPreference?.prefValue?.pinnedColumns);
			}
			onChange(pref.prefValue, { isDefault: pref.id === 0, preference: pref });
		}
	}, [onChange, state.ready]);


	const setActiveIndex = useCallback(
		(index) => {

			const activeIndex = index >= 0 && index < state.preferences?.length ? index : -1;
			const preference = activeIndex === -1 ? defaultPreference : state.preferences[activeIndex];
			const prefValue = preference?.prefValue;
			columnWidthsRef.current = prefValue?.columnWidths || columnWidthsRef.current;
			prefValue.columnWidths = columnWidthsRef.current;
			preference.prefValue = prefValue;

			setState((prevState) => {
				const newPrefs = [...prevState.preferences];
				newPrefs[activeIndex] = preference;
				return ({
					activeIndex: activeIndex,
					preferences: newPrefs,
					menuAnchorEl: null
				});		
			});
		},
		[state.preferences]
	);
	const fetchPreferences = useCallback(() => {
		if (!!versionedGridId && typeof versionedGridId === 'string') {
			setState({ fetching: true, ready: false });
			const formData = new FormData();
			formData.append('id', versionedGridId);
			formData.append('action', 'list');
			formData.append('limit', 100);
			return fetch(connectionService.GridPreferenceManager, {
				method: 'POST',
				headers: {
					'X-Background-Request': 'true'
				},
				body: formData,
				credentials: 'include'
			})
				.then((res) => res.json())
				.then(({ success, preferences: fetchedPreferences, totalCount }) => {
					let preferences = state.preferences;
					if (success && Array.isArray(fetchedPreferences)) {
						preferences = fetchedPreferences
							.map((fetchedPreference) => {
								return {
									...fetchedPreference,
									isDefault: Boolean(fetchedPreference.isDefault),
									prefValue: JSON.parse(fetchedPreference.prefValue)
								};
							})
							.sort((a, b) => a.prefName.localeCompare(b.prefName));
					}
					const activeIndex = preferences?.findIndex?.((pref) => pref.isDefault);

					setState({
						preferences: preferences,
						fetched: Boolean(success),
						activeIndex: activeIndex > -1 ? activeIndex : -1,
						ready: true
					});
					return preferences;
				})
				.catch((error) => {
					console.error('fetch Preferences error', error);
				})
				.finally(() => setState({ fetching: false, ready: true }));
		} else {
			setState({ ready: true, fetched: false, activeIndex: -1 });
		}
	}, [state.activeIndex, state.preferences, versionedGridId]);

	const savePreference = useCallback(
		async (pref, isReset = false) => {
			if (Array.isArray(pref.prefValue?.filter?.filterModel?.items)) {
				const filterModelItems = pref.prefValue.filter.filterModel.items.map(
					({ id, fromInput, ...filterItem }) => filterItem
				);
				pref.prefValue.filter.filterModel.items = filterModelItems;
			}

			const preference = { ...pref, isSystemDefault: false };
			if (typeof preference.prefId !== 'number') {
				preference.prefId = state.preferences?.length || 1;
			}
			if (!preference.id) {
				preference.id = 0;
			}
			let indexOfPref = state.preferences.findIndex((pref) => pref.id === preference.id);
			let newPrefs = JSON.parse(JSON.stringify(state.preferences));
			if (indexOfPref >= 0) {
				newPrefs[indexOfPref] = preference;
			} else {
				indexOfPref = newPrefs.length;
				newPrefs.push(preference);
			}
			newPrefs = newPrefs.sort((a, b) => a.prefName.localeCompare(b.prefName));

			const activeIndex = newPrefs.findIndex((pref) => pref.id && pref.id === currentPreference?.id);
			if (preference.isDefault && !isReset) {
				const resetPreferences = state.preferences.filter((entry) => entry.id !== preference.id && entry.isDefault);
				const resetSaves = resetPreferences.map((entry) => savePreference({ ...entry, isDefault: false }, true));
				if (resetSaves.length > 0) {
					await Promise.allSettled(resetSaves);
				}
			} else {
				setState({
					saving: preference.id,
					ready: !gridId,
					preferences: newPrefs,
					activeIndex: activeIndex
				});
			}

			if (!!versionedGridId && typeof versionedGridId === 'string' && !preference?.isSystemDefault) {
				const formData = new FormData();
				const loginData = util.localData.loginData;
				if (loginData?.tags?.ClientId) {
					formData.append('clientId', loginData.tags.ClientId);
				}
				formData.append('id', preference.id || 0);
				formData.append('GridId', versionedGridId);
				formData.append('prefName', preference.prefName);
				formData.append('isDefault', Boolean(preference.isDefault));
				formData.append('prefDesc', preference.prefDesc || '');
				formData.append('prefValue', JSON.stringify(preference.prefValue));
				formData.append('action', 'save');
				await fetch(connectionService.GridPreferenceManager, {
					body: formData,
					method: 'POST',
					credentials: 'include'
				})
					.catch((err) => {})
					.finally(() => {
						if (!isReset) {
							fetchPreferences();
						}
					});
			}
			setState({ saving: false, ready: true });
		},
		[gridId, state.activeIndex, state.preference, versionedGridId]
	);

	const deletePreferences = async (ids = []) => {
		if (ids?.length > 0) {
			setState({ deleting: true });
			const formData = new FormData();
			formData.append('action', 'delete');
			formData.append('ids', Array.isArray(ids) ? ids.join(',') : ids);
			await fetch(connectionService.GridPreferenceManager, {
				body: formData,
				method: 'POST',
				credentials: 'include'
			})
				.then((res) => res.json())
				.then((json) => fetchPreferences())
				.catch((err) => {
					console.error('Error deleting preferences: ', ids);
				})
				.finally(() => setState({ deleting: false }));
		}
	};

	const deleteSelectedPreference = useCallback(
		(id, dialogOpen = true) =>
			async () => {
				if (state.confirmDeleteOpen && state.selectedId) {
					deletePreferences(state.selectedId);
				}
				setState((prevState) => ({
					confirmDeleteOpen: !prevState.confirmDeleteOpen,
					selectedId: prevState.confirmDeleteOpen ? null : id,
					dialogOpen: dialogOpen
				}));
			},
		[state.confirmDeleteOpen, state.selectedId, setState]
	);
	const onCloseConfirmDelete = () => {
		setState({
			confirmDeleteOpen: false,
			selectedId: null
		});
	};

	const gridStateRef = useLiveRef({
		standaloneFilters,
		sorting: {
			sortModel
		},
		filter: {
			filterModel: filterModel
		},
		pagination: {
			pageSize,
			page
		},
		columns: {
			columnVisibilityModel,
			columnOrder: columnDefinations.map(({ field }) => field)
		},
		columnWidths: columnWidthsRef.current,
		pinnedColumns
	});

	const syncPreference = useCallback(
		async (pref) => {
			setState({ dialogOpen: false });
			const preference = {
				...pref,
				prefValue: {
					...pref?.prefValue,
					...gridStateRef.current,
					columnWidths: columnWidthsRef.current,
				}
			};
			await savePreference(preference);
		},
		[savePreference, gridStateRef.current]
	);

	const initialValues = {
		prefName: '',
		prefDesc: '',
		isDefault: false
	};
	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: (values) => {
			syncPreference(values);
		}
	});
	const handleOpen = (event) => {
		setState({ menuAnchorEl: event?.currentTarget });
	};
	const handleClose = () => {
		setState({ menuAnchorEl: null });
	};
	const handleSelectItem = (index) => () => {
		setActiveIndex(index);
	};
	const handleAdd = () => {
		formik.resetForm();
		setState({ dialogOpen: true, dialogContent: 'form', dialogWidth: 'sm' });
	};
	const handleDialogClose = () => {
		setState({ dialogOpen: false });
	};

	const handleOnManage = () => {
		setState({ dialogOpen: true, dialogContent: 'list', dialogWidth: 'md' });
	};

	const saveGridStateAsPrefValue = useCallback(
		(pref) => () => {
			if (pref?.id) {
				const preference = {
					...pref,
					prefValue: { ...gridStateRef.current, columnWidths: columnWidthsRef.current }
				};
				savePreference(preference);
			}
		},
		[]
	);
	const columns = useMemo(
		() => [
			{
				field: 'prefName',
				headerName: 'Name',
				width: 160
			},
			{
				field: 'prefDesc',
				headerName: strings.Description || 'Description',
				flex: 1
			},
			{
				field: 'isDefault',
				headerName: strings.IsDefaultText || 'Is Default ?',
				type: 'boolean'
			},
			{
				field: 'delete',
				type: 'actions',
				width: 80,
				getActions: ({ id, row }) => [
					<GridActionsCellItem
						icon={<DeleteIcon fontSize="inherit" />}
						color="error"
						data-id={id}
						title={strings?.Delete}
						label={strings?.Delete || 'Delete'}
						onClick={deleteSelectedPreference(id)}
						key={id}
					/>
				]
			},
			{
				field: 'save',
				header: strings?.Save,
				type: 'actions',
				width: 80,
				getActions: ({ id, row }) => [
					<GridActionsCellItem
						key={id}
						icon={<SaveIcon fontSize="inherit" />}
						color="success"
						data-id={id}
						label={strings.Save}
						title={strings.Save}
						onClick={saveGridStateAsPrefValue(row)}
					/>
				]
			}
		],
		[]
	);
	const handleOnCellDoubleClick = (event) => {
		const { row: record } = event;
		formik.setValues(record);
		setState({ dialogOpen: true, dialogContent: 'form', dialogWidth: 'md' });
	};

	const handleOnColumnWidthChangeEvent = useCallback(() => {
		const allColumns = apiRef.current.getAllColumns() || [];		
		const columnWidths = allColumns.reduce((acc, { field, width }) => acc.concat({ field, width }), []);
		columnWidthsRef.current = columnWidths;
	},[]);

	useUniqueEffect(() => {
		setState(initialState);
		fetchPreferences();
		apiRef.current.subscribeEvent(
			'columnResizeStop',
			handleOnColumnWidthChangeEvent,
		);
		return () => {
			if (apiRef.current.unsubscribeEvent) {
				apiRef.current.unsubscribeEvent("columnResizeStop", handleOnColumnWidthChangeEvent);
			}
		};
	}, [versionedGridId]);

	useDidUpdate(() => {
		handleOnChange(currentPreference, { isDefault: currentPreference?.id === 0, preference: currentPreference })
	}, [currentPreference]);

	const getRowClassName = useCallback(
		({ id }) => {
			const pristine = util.deepEqual(currentPreference?.prefValue, gridStateRef.current);
			let className = currentPreference && id === currentPreference?.id ? 'Active-Preference' : '';
			className += ` ${!pristine ? 'Unsaved' : ''}`;
			return className;
		},
		[currentPreference]
	);

	const loading = !!(state.fetching || state.saving || state.deleting);

	return (
		<Box>
			<Button
				id="grid-preferences-btn"
				aria-controls={state.menuAnchorEl ? 'basic-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={state.menuAnchorEl ? 'true' : undefined}
				onClick={handleOpen}
				color="error"
				variant="contained"
				title={currentPreference?.prefName}
				startIcon={loading ? <CircularProgress size={'1rem'} color="inherit" /> : <SettingsIcon />}
				sx={{
					backgroundColor: `${theme.palette.text.primary} !important`
				}}
			>
				{strings.preferences || 'Preferences'}
			</Button>
			<Menu
				id={`grid-preference-menu`}
				anchorEl={state.menuAnchorEl}
				open={!!state.menuAnchorEl}
				onClose={handleClose}
				component={List}
				dense
				MenuListProps={{
					'aria-labelledby': 'grid-preferences-btn'
				}}
				sx={{
					'& .MuiMenu-paper': { minWidth: 240, maxHeight: 320 },
					'& .MuiListItemSecondaryAction-root': {
						display: 'flex'
					},
					'& .Mui-selected': {
						color: 'text.primary',
						backgroundColor: `${theme.palette.action.selected} !important`,
						'&:hover': {
							backgroundColor: 'success.main'
						}
					}
				}}
			>
				<MenuItem component={ListItemButton} onClick={handleAdd} dense>
					{strings.Add ?? 'Add'}
				</MenuItem>
				<MenuItem component={ListItemButton} onClick={handleOnManage} dense>
					{strings.ManagePreferences ?? 'Manage Preferences'}
				</MenuItem>
				<MenuItem
					onClick={() => setState({ activeIndex: -1 })}
					component={ListItem}
					selected={state.activeIndex === -1}
					secondaryAction={
						state.activeIndex === -1 ? (
							<CheckIcon fontSize="small" />
						) : (
							defaultPreferenceIndex === -1 && <BookmarkAddedIcon />
						)
					}
					title={defaultPreference.prefDesc}
					className="defaultPreference"
					dense
					divider
				>
					<ListItemText primary={defaultPreference.prefName} />
				</MenuItem>

				{state.preferences.map(({ id, prefName, ...option }, i) => (
					<MenuItem
						onClick={handleSelectItem(i)}
						component={ListItem}
						key={`pref-item-${i}`}
						selected={i === state.activeIndex}
						secondaryAction={
							state.saving === id ? (
								<CircularProgress size={16} color="inherit" />
							) : i === state.activeIndex ? (
								<CheckIcon fontSize="small" />
							) : (
								defaultPreferenceIndex === i && <BookmarkAddedIcon fontSize="small" />
							)
						}
						title={option.prefDesc}
						dense
					>
						<ListItemText primary={prefName} />
					</MenuItem>
				))}
			</Menu>
			<Dialog open={state.dialogOpen} onClose={handleDialogClose} maxWidth={state.dialogWidth} fullWidth>
				<DialogTitle>
					<Stack direction="row" columnGap={2}>
						{state.dialogContent === 'form' && !!formik.values?.id && (
							<IconButton size="small" color="warning" onClick={handleOnManage}>
								<ArrowBackIcon fontSize="inherit" />
							</IconButton>
						)}
						<Typography variant="subtitle1" sx={{ mr: 1 }}>
							{state.dialogContent === 'list'
								? strings.ManagePreferences
								: formik.values?.id
								? strings.Edit || 'Edit'
								: strings.Add || 'Add'}
						</Typography>
						{state.dialogContent === 'list' && (
							<Stack direction="row" columnGap={1}>
								<Button
									startIcon={<AddIcon />}
									variant="contained"
									size="small"
									onClick={handleAdd}
									color="primary"
									disableElevation
								>
									{strings.Add || 'Add'}
								</Button>
							</Stack>
						)}
					</Stack>
				</DialogTitle>
				<DialogContent>
					{state.dialogContent === 'form' && (
						<Grid
							component={'form'}
							onSubmit={formik.handleSubmit}
							rowGap={2}
							container
							sx={{
								'& .MuiFormLabel-root:not(.MuiTypography-root)': {
									color: 'primary.light',
									fontWeight: 600,
									fontSize: '0.95rem',
									display: 'table',
									whiteSpace: 'pre-wrap' /* css-3 */,
									wordWrap: 'break-word' /* Internet Explorer 5.5+ */,
									marginRight: theme.spacing(0.5),
									'& .MuiFormLabel-asterisk': {
										color: 'error.light'
									}
								}
							}}
						>
							<Grid  size={12}>
								<TextField
									defaultValue={formik.values.prefName}
									variant="outlined"
									size="small"
									margin="dense"
									label={strings.Name}
									name={'prefName'}
									onChange={formik.handleChange}
									error={!!formik.errors.prefName}
									helperText={formik.errors.prefName}
									required
									fullWidth
								/>
							</Grid>
							<Grid size={12}>
								<TextField
									defaultValue={formik.values.prefDesc}
									variant="outlined"
									multiline
									rows={2}
									size="small"
									margin="dense"
									label={strings.Description || 'Description'}
									name={'prefDesc'}
									onChange={formik.handleChange}
									error={!!formik.errors.prefDesc}
									helperText={formik.errors.prefDesc}
									fullWidth
								/>
							</Grid>
							<Grid size={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formik.values.isDefault}
											name={'isDefault'}
											onChange={formik.handleChange}
										/>
									}
									label={strings.IsDefaultText || 'Is Default ?'}
								/>
							</Grid>
							<Grid size={12}>
								<Stack direction="row" columnGap={1}>
									<Button
										type="submit"
										size="small"
										startIcon={<SaveIcon />}
										color="success"
										variant="contained"
										disableElevation
									>
										{strings.Save}
									</Button>
									<Button
										type="button"
										startIcon={<CloseIcon />}
										color="warning"
										variant="contained"
										size="small"
										onClick={handleDialogClose}
										disableElevation
									>
										{strings.Cancel}
									</Button>
								</Stack>
							</Grid>
						</Grid>
					)}
					{state.dialogContent === 'list' && state.dialogOpen && (
						<Grid container>
							<Grid size={12}>
								<DataGridPremium
									columns={columns}
									rows={state.preferences}
									rowCount={state.preferences?.length || 0}
									density="compact"
									onCellDoubleClick={handleOnCellDoubleClick}
									loading={loading}
									components={{
										LoadingOverlay: LinearProgress
									}}
									sx={{
										'& .Active-Preference': {
											backgroundColor: theme.palette.action.selected,
											fontWeight: 'bold',
											'&.Unsaved': {
												fontStyle: 'italic'
											}
										}
									}}
									disableSelectionOnClick
									componentsProps={{
										filterPanel: {
											// No Link operators,
											linkOperators: [],
											filterFormProps: {
												linkOperatorInputProps: {
													variant: 'outlined',
													size: 'small'
												},
												columnInputProps: {
													variant: 'outlined',
													size: 'small',
													sx: { mt: 'auto' }
												},
												operatorInputProps: {
													variant: 'outlined',
													size: 'small',
													sx: { mt: 'auto' }
												},
												valueInputProps: {
													InputComponentProps: {
														variant: 'outlined',
														size: 'small'
													}
												}
											},
											sx: {
												'& .MuiDataGrid-filterForm': { p: 2 },
												'& .MuiDataGrid-cell': {
													cursor: 'pointer'
												},
												'& .MuiDataGrid-filterFormLinkOperatorInput': { mr: 2 },
												'& .MuiDataGrid-filterFormColumnInput': { mr: 2, width: 150 },
												'& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
												'& .MuiDataGrid-filterFormValueInput': { width: 200 },
												'& .MuiDataGrid-menu': {
													zIndex: (theme) => `${theme.zIndex.tooltip} !important`
												},
												'& .MuiDataGrid-panel': {
													zIndex: (theme) => `${theme.zIndex.tooltip} !important`
												}
											}
										}
									}}
									getRowClassName={getRowClassName}
									disableRowSelectionOnClick
									disableColumnMenu
									localeText={{
										noRowsLabel: strings.NoRows || 'No rows',
										columnHeaderName: strings.Name || 'Name'
									}}
								/>
							</Grid>
						</Grid>
					)}
					<ConfirmDialog
						title={strings.Delete}
						body={strings.DeleteAlertText}
						open={!!state.selectedId && state.confirmDeleteOpen}
						cancelButtonText={strings.Cancel}
						okButtonText={strings.Delete}
						onCancel={onCloseConfirmDelete}
						onConfirm={deleteSelectedPreference(state.selectedId)}
					/>
				</DialogContent>
				{state.dialogContent === 'list' && (
					<DialogActions>
						<Button color="error" variant="contained" size="small" onClick={handleDialogClose} disableElevation>
							{strings.Close}
						</Button>
					</DialogActions>
				)}
			</Dialog>
		</Box>
	);
};
export default memo(GridPreferencesControl);
